import React, { useState } from "react"
import {
  Box,
  IconButton,
  Menu,
  Chip,
  TextField,
  Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import CloseIcon from "@mui/icons-material/Close"
import DeleteIcon from "@mui/icons-material/Delete"
import PropTypes from "prop-types"
import { colorPalette } from "./constants"

const TagField = ({
  options = [],
  selectedTags = [],
  onChange,
  onCreateTag,
  onDeleteTag,
  multiple = true,
  label = "",
  size = "medium",
  borderless = false,
  variant = "default",
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [showCreateInput, setShowCreateInput] = useState(false)
  const [newTagName, setNewTagName] = useState("")

  const isGrid = variant === "grid"

  const handleClick = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setShowCreateInput(false)
    setNewTagName("")
  }

  const handleAddTag = tag => {
    if (!selectedTags.some(selected => selected.SK === tag.SK)) {
      if (!multiple && selectedTags.length > 0) {
        // Replace the current tag if multiple is false
        onChange([tag])
      } else {
        onChange([...selectedTags, tag])
      }
    }
    handleClose()
  }

  const handleToggleCreateInput = () => {
    setShowCreateInput(!showCreateInput)
    if (!showCreateInput) {
      setNewTagName("")
    }
  }

  const handleCreateTag = () => {
    if (newTagName.trim() && onCreateTag) {
      const randomColor =
        colorPalette[Math.floor(Math.random() * colorPalette.length)]
      onCreateTag(newTagName.trim(), randomColor)
      setNewTagName("")
      setShowCreateInput(false)
    }
  }

  const handleDeleteTag = tag => {
    if (onDeleteTag) {
      onDeleteTag(tag)
    }
  }

  return (
    <Box
      sx={{
        border: borderless ? "none" : "1px solid #bdbdbd",
        borderRadius: 1,
        p: isGrid ? 0 : 2,
        height: isGrid ? "100%" : "56px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          gap: 1,
          overflowX: "auto",
          overflowY: "hidden",
          alignItems: "center",
        }}
      >
        {selectedTags.length > 0 ? (
          selectedTags.map(tag => (
            <Box
              key={tag.SK}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <Chip
                label={tag.properties.name}
                size="small"
                sx={{
                  fontSize: 12,
                  borderRadius: "2px",
                  backgroundColor: tag.properties.color,
                }}
              />
              <IconButton
                size="small"
                onClick={() =>
                  onChange(selectedTags.filter(t => t.SK !== tag.SK))
                }
                sx={{ padding: 0, width: 14, height: 14 }}
              >
                <CloseIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
          ))
        ) : (
          <Typography variant="body2" sx={{ color: "#616161", fontSize: 16 }}>
            {label}
          </Typography>
        )}
      </Box>
      <IconButton
        size="small"
        onClick={handleClick}
        disabled={!multiple && selectedTags.length > 0}
        sx={{
          backgroundColor: "#eeeeee",
          borderRadius: "4px",
          padding: "3px",
          marginLeft: 1,
          flexShrink: 0,
          opacity: !multiple && selectedTags.length > 0 ? 0.5 : 1,
        }}
      >
        <AddIcon sx={{ fontSize: 16 }} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              width: 180,
              maxHeight: 200,
              overflowY: "auto",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "end",
            px: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={handleToggleCreateInput}
            sx={{
              padding: "3px",
              flexShrink: 0,
            }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {showCreateInput && (
          <Box sx={{ px: 2, pt: 0 }}>
            <TextField
              size="small"
              placeholder="Nueva etiqueta"
              value={newTagName}
              onChange={e => setNewTagName(e.target.value)}
              onKeyPress={e => {
                if (e.key === "Enter") {
                  handleCreateTag()
                }
              }}
              autoFocus
              fullWidth
              inputProps={{
                maxLength: 15,
                style: { fontSize: 12, padding: "6px 8px" },
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            p: 1,
          }}
        >
          {options.map(tag => (
            <Box
              key={tag.SK}
              sx={{
                px: 1,
                ":hover .delete-tag-btn": {
                  opacity: 1,
                  pointerEvents: "auto",
                },
              }}
            >
              <Chip
                label={tag.properties.name}
                size="small"
                onClick={() => handleAddTag(tag)}
                sx={{
                  fontSize: 12,
                  borderRadius: "2px",
                  backgroundColor: `${tag.properties.color} !important`,
                }}
              />
              <IconButton
                className="delete-tag-btn"
                size="small"
                onClick={() => handleDeleteTag(tag)}
                sx={{
                  opacity: 0,
                  p: 0.5,
                  pointerEvents: "none",
                  transition: "opacity 0.2s",
                  position: "absolute",
                  right: 6,
                }}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Menu>
    </Box>
  )
}

TagField.propTypes = {
  options: PropTypes.arrayOf(PropTypes.object),
  selectedTags: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func.isRequired,
  onCreateTag: PropTypes.func,
  onDeleteTag: PropTypes.func,
  multiple: PropTypes.bool,
  label: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium"]),
  borderless: PropTypes.bool,
  variant: PropTypes.oneOf(["default", "grid"]),
}

export default TagField
